import React from 'react';
import styled from 'styled-components';
import { Container, Button, Box } from '@mui/material';
import { Link } from 'react-router-dom-v5';
import { colors } from 'spw-theme';
import { SPWBaseTextField, SPWIconButton } from 'spw-shared/globalComponents';
import { omit } from 'lodash';

export const GradientBackgroundContainer = styled(Container)`
  && {
    position: absolute;
    width: 100%;
    max-width: 100% !important;
    top: 0;
    height: 100vh;
    background: linear-gradient(
      180deg,
      rgba(11, 149, 178, 1) 0%,
      #78e4d4 13.02%,
      #d8fff1 68.23%,
      rgba(216, 255, 241, 0) 100%
    );
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
`;

export const DialogBox = styled.div.withConfig({
  shouldForwardProp: (prop) => !['error', 'success'].includes(prop),
})<{
  error?: boolean;
  success?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
  border-radius: 5px;

  ${({ error }) => error && `border-color: ${colors.red};`}
  ${({ success }) =>
    success && `border-color: ${colors.mainGreen};`}
  & .icon-image {
    height: 80px;
  }

  & .title {
    font-weight: 500;
    font-size: 24px;
    line-height: 32px;
    margin: 16px 0 24px;
    ${({ error }) => error && `color: ${colors.red}`}
    ${({ success }) => success && `color: ${colors.mainGreen}`}
  }
`;

export const DialogCustom = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isChangePasswordContainer'].includes(prop),
})<{
  isChangePasswordContainer?: boolean;
}>`
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  width: 420px;
  background-color: #ffffff;
  border-radius: 5px;
  position: relative;
  ${({ isChangePasswordContainer }) =>
    isChangePasswordContainer
      ? `padding: 64px 30px 56px;`
      : `padding: 64px 30px;`}
`;

export const FormColumns = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

export const Title = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 56px;
  text-transform: uppercase;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 24px;
  letter-spacing: 0.2em;
`;

export const LoginDialogText = styled.div`
  margin: 2px 0px 39px 0px;
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: #000000;
`;

export const SecondaryButton = styled(Link)`
  color: ${colors.mainBlue};
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
`;

export const TextField = styled(SPWBaseTextField)`
  && {
    width: 100%;
    height: unset;
  }

  && .MuiFormHelperText-root {
    font-size: 12px;
    line-height: 16px;
    margin: 4.5px 0 11.5px 9px;
    letter-spacing: 0.4px;
  }

  && .MuiInputBase-input {
    height: 56px;
  }

  & .MuiOutlinedInput-adornedEnd {
    padding-right: 4px;
  }

  & .MuiInputBase-input::placeholder {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-size: 18px !important;
    line-height: 24px !important;
    color: ${colors.greyInputAddornment} !important;
  }
`;

export const ErrorMessage = styled((props) => (
  <Box {...omit(props, 'noLetterSpacing')} />
))`
  font-size: 14px;
  line-height: 16px;

  ${(props) =>
    !props.noLetterSpacing
      ? `letter-spacing: 0.4px;`
      : `
        display: flex;
        align-items: center;
        justify-content: space-between;
      `}

  color: ${colors.red};

  position: absolute;
  text-align: center;
  width: 100%;
`;

export const ButtonCustom = styled(Button)`
  width: 100%;
  height: 48px;
  color: #ffffff;
  background-color: #12819b;

  font-weight: 500;
  font-size: 16px;
  line-height: 22px;

  && {
    &.modal {
      width: 173px;
      height: 36px;
    }
  }

  &&:hover {
    color: #ffffff;
    background-color: #0e6d83;
  }

  a {
    color: #fff;
    text-decoration: initial;
  }
`;

export const PasswordResetSuccessMessage = styled.div`
  width: 482px;
  box-sizing: border-box;
  padding: 20px 39px 20px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.white};
  border: 1px solid ${colors.mainGreen};
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 18px;
  line-height: 24px;

  .green-check {
    margin-right: 8px;
  }
`;

export const LogoutToast = styled.div`
  && {
    height: 52px;
    width: 480px;
    background: ${colors.white};
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-sizing: border-box;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
    border-radius: 4px;

    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 24px;

    font-family: Roboto;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: ${colors.black};
  }
`;

export const PasswordRequirementsCard = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.veryLightGray};
  border-radius: 8px;
  padding: 16px 20.4px 24px 0;
  color: ${colors.black};
  margin: 8px 0 56px;

  font-family: 'Roboto';
  font-style: normal;
  font-size: 14px;
  line-height: 20px;

  .password-requirements-title {
    font-weight: 500;
    padding: 0 0 22px 24px;
  }

  .content {
    display: flex;
    justify-content: space-between;
    font-weight: 400;

    ul {
      box-sizing: border-box;
      width: 278px;
      margin: 0;
      padding-left: 33px;
    }
  }
`;

export const LockoutText = styled.div`
  font-weight: 500;
  font-size: 24px;
  line-height: 40px;
  text-align: center;
  width: 420px;
`;

export const LockoutFooterText = styled.div`
  font-weight: 400;
  font-size: 18px;
  line-height: 24px;
  margin-top: 36px;
  text-align: center;
  width: 400px;
`;

export const CloseButton = styled(SPWIconButton)`
  && {
    position: absolute;
    top: 16px;
    right: 16px;
  }
`;
